export type Point = [x: number, y: number];
// A face of the die and how much light it takes: -0.4 (shaded) to +0.3 (lit), from the top left.
export type Face = [points: Point[], shade: number];

// A thrown die drawn flat in a 100×100 box: its outline, the faces lit over it, a gloss on the
// lit edge, and where its number goes: [centre x, centre y, font size for 1, 2 and 3 digits].
export type DieShape = {
	outline: Point[];
	faces: Face[];
	gloss: Point[];
	numeral: [x: number, y: number, one: number, two: number, three: number];
};

const round = (value: number) => Math.round(value * 10) / 10;

const ring = (cx: number, cy: number, r: number): Point[] =>
	Array.from({ length: 36 }, (_, k): Point => [round(cx + r * Math.cos((k * 10 * Math.PI) / 180)), round(cy + r * Math.sin((k * 10 * Math.PI) / 180))]);

const H: Point[] = [
	[50, 3],
	[93, 27],
	[93, 73],
	[50, 97],
	[7, 73],
	[7, 27],
];
const T: Point[] = [
	[50, 24],
	[78, 70],
	[22, 70],
];
const O: Point[] = [
	[50, 5],
	[95, 37],
	[78, 93],
	[22, 93],
	[5, 37],
];
const I: Point[] = [
	[50, 25],
	[73, 42],
	[64, 70],
	[36, 70],
	[27, 42],
];
const K = { A: [50, 4], R: [93, 44], B: [50, 96], L: [7, 44], l: [28, 52], c: [50, 66], r: [72, 52] } as Record<string, Point>;
const D = { A: [50, 4], R: [94, 50], B: [50, 96], L: [6, 50], fl: [22, 58], fr: [78, 58] } as Record<string, Point>;

const KITE: Point[] = [K.A, K.R, K.B, K.L];
const KITE_FACES: Face[] = [
	[[K.A, K.r, K.c, K.l], 0.06],
	[[K.A, K.l, K.L], 0.3],
	[[K.A, K.R, K.r], -0.14],
	[[K.L, K.l, K.c, K.B], -0.1],
	[[K.r, K.R, K.B, K.c], -0.34],
];
const KITE_GLOSS: Point[] = [
	[12, 43],
	[46, 10],
	[29, 48],
];

const CUBE: DieShape = {
	outline: [
		[10, 14],
		[14, 10],
		[86, 10],
		[90, 14],
		[90, 86],
		[86, 90],
		[14, 90],
		[10, 86],
	],
	faces: [
		[
			[
				[10, 14],
				[14, 10],
				[86, 10],
				[90, 14],
				[79, 20],
				[21, 20],
			],
			0.3,
		],
		[
			[
				[10, 86],
				[10, 14],
				[21, 20],
				[21, 80],
			],
			0.12,
		],
		[
			[
				[90, 14],
				[90, 86],
				[79, 80],
				[79, 20],
			],
			-0.2,
		],
		[
			[
				[90, 86],
				[86, 90],
				[14, 90],
				[10, 86],
				[21, 80],
				[79, 80],
			],
			-0.34,
		],
		[
			[
				[21, 20],
				[79, 20],
				[79, 80],
				[21, 80],
			],
			0.04,
		],
	],
	gloss: [
		[16, 13],
		[84, 13],
		[76, 18],
		[24, 18],
	],
	numeral: [50, 46.75, 69.8, 40, 30],
};

const SHAPES: Record<number, DieShape> = {
	2: {
		outline: ring(50, 50, 46),
		faces: [
			[ring(50, 50, 46), -0.16],
			[ring(47, 47, 37), 0.12],
		],
		gloss: [],
		numeral: [45.75, 43.75, 81, 34, 26],
	},
	3: CUBE,
	4: {
		outline: [
			[50, 6],
			[95, 88],
			[5, 88],
		],
		faces: [
			[
				[
					[50, 6],
					[60, 88],
					[5, 88],
				],
				0.08,
			],
			[
				[
					[50, 6],
					[95, 88],
					[60, 88],
				],
				-0.26,
			],
		],
		gloss: [
			[47, 12],
			[50, 9],
			[14, 80],
			[10, 84],
		],
		numeral: [40, 65.5, 47.2, 28, 21],
	},
	6: CUBE,
	8: {
		outline: [D.A, D.R, D.B, D.L],
		faces: [
			[[D.A, D.fr, D.fl], 0.06],
			[[D.A, D.fl, D.L], 0.3],
			[[D.A, D.R, D.fr], -0.14],
			[[D.fl, D.fr, D.B], -0.24],
			[[D.L, D.fl, D.B], -0.08],
			[[D.fr, D.R, D.B], -0.38],
		],
		gloss: [
			[11, 50],
			[46, 10],
			[23, 54],
		],
		numeral: [49.75, 40.5, 36.7, 28, 22],
	},
	10: { outline: KITE, faces: KITE_FACES, gloss: KITE_GLOSS, numeral: [49.75, 41.75, 35.2, 26.3, 22] },
	12: {
		outline: O,
		faces: [
			[[O[0], O[1], I[1], I[0]], 0.14],
			[[O[1], O[2], I[2], I[1]], -0.22],
			[[O[2], O[3], I[3], I[2]], -0.34],
			[[O[3], O[4], I[4], I[3]], -0.12],
			[[O[4], O[0], I[0], I[4]], 0.3],
			[I, 0.05],
		],
		gloss: [
			[10, 37],
			[48, 10],
			[48, 23],
			[27, 39],
		],
		numeral: [50, 50.5, 40.9, 32.3, 25],
	},
	20: {
		outline: H,
		faces: [
			[[H[5], H[0], T[0]], 0.3],
			[[H[0], H[1], T[0]], 0.14],
			[[H[1], T[1], T[0]], -0.04],
			[[H[1], H[2], T[1]], -0.22],
			[[H[2], H[3], T[1]], -0.34],
			[[H[3], T[2], T[1]], -0.18],
			[[H[3], H[4], T[2]], -0.28],
			[[H[4], H[5], T[2]], -0.08],
			[[H[5], T[0], T[2]], 0.16],
			[[T[0], T[1], T[2]], 0.06],
		],
		gloss: [
			[12, 28],
			[48, 8],
			[48, 20],
		],
		numeral: [50, 54.25, 33, 24.6, 20],
	},
	// One die showing 1–100, as DiceRoll stores it, never a pair of d10s.
	100: { outline: KITE, faces: KITE_FACES, gloss: KITE_GLOSS, numeral: [49.75, 41.75, 35.2, 22.5, 18.8] },
};

const PLAIN: DieShape = { outline: H, faces: [], gloss: [], numeral: [49.5, 46.25, 77.6, 61.4, 42.9] };

export function dieShape(sides: number): DieShape {
	return SHAPES[sides] ?? PLAIN;
}

// A die lies at its own slant: the same number on the same die always tilts the same way.
export function dieTilt(sides: number, value: number) {
	return ((value * 37 + sides * 11) % 25) - 12;
}

export function points(list: Point[]) {
	return list.map(([x, y]) => `${x},${y}`).join(" ");
}
